import { NODE_HANDLE_IDS, NODE_TYPES } from '@flow/nodes';
import { logger } from '@utils/usefulFuncs';

export class AgentScriptGenerationHelpers {
  constructor(nodes, edges) {
    this.nodes = nodes;
    this.edges = edges;
  }

  generateJsonFromNodesAndEdges = () => {
    const plan_steps = this.generatePlanStepJson();
    const step_output_priority_order = this.generateStepOutputPriorityOrderJson();

    const json = {
      plan_steps,
      final_output_aggregation: { step_output_priority_order },
    };
    return json;
  };

  generatePlanStepJson = () => {
    const startNode = this._findStartNode();

    const generatedPlanStepNodes = [];
    const firstNode = this._findConnectedNode(startNode.id, NODE_HANDLE_IDS.planStep);
    if (!firstNode) {
      logger('generatePlanStepJson: No node connected with start node', {
        nodes: this.nodes,
        edges: this.edges,
        startNode,
      });
      return;
    }
    generatedPlanStepNodes.push(this.generatePlanStepJsonForNode(firstNode));
    this._generateRemainingPlanStepNodes(firstNode, generatedPlanStepNodes);
    return generatedPlanStepNodes;
  };

  generateStepOutputPriorityOrderJson = () => {
    const startNode = this._findStartNode();

    const generatedFinalOutputAggregationNodes = [];
    const firstNode = this._findConnectedNode(startNode.id, NODE_HANDLE_IDS.outputAggregation);
    if (!firstNode) {
      logger('generateFinalOutputAggregationJson: No node connected with start node', {
        nodes: this.nodes,
        edges: this.edges,
        startNode,
      });
      return;
    }
    generatedFinalOutputAggregationNodes.push(this.generateFinalOutputAggregationJsonForNode(firstNode));
    this._generateRemainingFinalOutputAggregationNodes(firstNode, generatedFinalOutputAggregationNodes);
    return generatedFinalOutputAggregationNodes;
  };

  generateFinalOutputAggregationJsonForNode = (node) => {
    const { data } = node;
    const { stepOutputPriorityOrder } = data;

    // If it's not an object (e.g., just a string step ID), return as-is
    if (typeof stepOutputPriorityOrder !== 'object' || stepOutputPriorityOrder === null) {
      return stepOutputPriorityOrder;
    }

    // Deep clone to avoid mutating original data
    const result = JSON.parse(JSON.stringify(stepOutputPriorityOrder));

    // Convert empty string output_element to null in output_value
    if (result.output_value && result.output_value.output_element === '') {
      result.output_value.output_element = null;
    }

    // Convert empty string output_element to null in condition
    if (result.condition && result.condition.output_element === '') {
      result.condition.output_element = null;
    }

    return result;
  };

  generatePlanStepJsonForNode = (node) => {
    const { data } = node;
    const { planStepData } = data;

    const { conditional_next_step_ids } = planStepData;

    let nextStepData = {};
    if (conditional_next_step_ids) {
      const conditionalYesNextStepNodes = this._findConnectedNode(node.id, NODE_HANDLE_IDS.planStepYes);
      conditional_next_step_ids.Yes = conditionalYesNextStepNodes
        ? [conditionalYesNextStepNodes.data.planStepData.step_id]
        : [];
      const conditionalNoNextStepNodes = this._findConnectedNode(node.id, NODE_HANDLE_IDS.planStepNo);
      conditional_next_step_ids.No = conditionalNoNextStepNodes
        ? [conditionalNoNextStepNodes.data.planStepData.step_id]
        : [];
    } else {
      const nextStepNode = this._findConnectedNode(node.id, NODE_HANDLE_IDS.planStep);
      nextStepData = {
        next_step_ids: nextStepNode ? [nextStepNode.data.planStepData.step_id] : [],
      };
    }

    return { ...planStepData, ...nextStepData };
  };

  _generateRemainingPlanStepNodes = (firstNode, generatedPlanStepNodes) => {
    let currentNode = firstNode;
    while (currentNode) {
      const { conditional_next_step_ids } = currentNode.data.planStepData;
      if (conditional_next_step_ids) {
        const { Yes, No } = conditional_next_step_ids;
        if (Yes.length > 0) {
          const yesNextNode = this._findConnectedNode(currentNode.id, NODE_HANDLE_IDS.planStepYes);
          generatedPlanStepNodes.push(this.generatePlanStepJsonForNode(yesNextNode));
          this._generateRemainingPlanStepNodes(yesNextNode, generatedPlanStepNodes);
        }
        if (No.length > 0) {
          const noNextNode = this._findConnectedNode(currentNode.id, NODE_HANDLE_IDS.planStepNo);
          generatedPlanStepNodes.push(this.generatePlanStepJsonForNode(noNextNode));
          this._generateRemainingPlanStepNodes(noNextNode, generatedPlanStepNodes);
        }
        break;
      } else {
        const nextNode = this._findConnectedNode(currentNode.id, NODE_HANDLE_IDS.planStep);
        if (!nextNode) {
          break;
        }
        generatedPlanStepNodes.push(this.generatePlanStepJsonForNode(nextNode));
        currentNode = nextNode;
      }
    }
  };

  _generateRemainingFinalOutputAggregationNodes = (firstNode, generatedFinalOutputAggregationNodes) => {
    let currentNode = firstNode;
    while (currentNode) {
      const nextNode = this._findConnectedNode(currentNode.id, NODE_HANDLE_IDS.outputAggregation);
      if (!nextNode) {
        break;
      }
      generatedFinalOutputAggregationNodes.push(this.generateFinalOutputAggregationJsonForNode(nextNode));
      currentNode = nextNode;
    }
  };

  _findStartNode = () => {
    return this.nodes.filter((node) => node.type === NODE_TYPES.AgentScriptStartNode)[0];
  };

  _findConnectedNode = (nodeId, handleId) => {
    const connectedEdges = this.edges.filter((edge) => edge.source === nodeId && edge.sourceHandle === handleId);
    if (!connectedEdges.length) {
      return null;
    }
    const connectedNodes = connectedEdges
      .map((edge) => this.nodes.find((node) => node.id === edge.target))
      .filter((node) => !!node);
    return connectedNodes[0];
  };
}
